import { useCallback } from 'react';
import { ViewMode } from '@/shared/types/datepicker';

export const useCalendar = (
  currentDate: Date,
  onNavigate: (direction: 'prev' | 'next') => void,
  onViewModeChange: (mode: ViewMode) => void,
) => {
  const navigate = useCallback(
    (direction: 'prev' | 'next') => {
      const newDate = new Date(currentDate);

      switch (direction) {
        case 'prev':
          newDate.setMonth(newDate.getMonth() - 1);
          break;
        case 'next':
          newDate.setMonth(newDate.getMonth() + 1);
          break;
      }

      onNavigate(direction);
    },
    [currentDate, onNavigate],
  );

  const getDaysInMonth = useCallback((date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startOfWeek = firstDay.getDay();

    return { firstDay, lastDay, daysInMonth, startOfWeek };
  }, []);

  const getCalendarDays = useCallback(
    (date: Date, startOfWeek: number = 1) => {
      const { firstDay, lastDay, daysInMonth, startOfWeek: monthStartDay } = getDaysInMonth(date);

      const days: (number | null)[] = [];

      // Ajustar el inicio de la semana
      const adjustedStartDay = (monthStartDay - startOfWeek + 7) % 7;

      // Agregar días vacíos al inicio
      for (let i = 0; i < adjustedStartDay; i++) {
        days.push(null);
      }

      // Agregar días del mes
      for (let day = 1; day <= daysInMonth; day++) {
        days.push(day);
      }

      // Completar la última semana si es necesario
      const remainingDays = (7 - (days.length % 7)) % 7;
      for (let i = 0; i < remainingDays; i++) {
        days.push(null);
      }

      return days;
    },
    [getDaysInMonth],
  );

  const getMonthNames = useCallback((locale: string = 'es-ES') => {
    const months = [];
    for (let i = 0; i < 12; i++) {
      const date = new Date(2024, i, 1);
      months.push(date.toLocaleDateString(locale, { month: 'long' }));
    }
    return months;
  }, []);

  const getYearRange = useCallback((currentYear: number, range: number = 12) => {
    const years = [];
    const startYear = currentYear - Math.floor(range / 2);

    for (let i = 0; i < range; i++) {
      years.push(startYear + i);
    }

    return years;
  }, []);

  const isToday = useCallback((date: Date) => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  }, []);

  const formatDate = useCallback((date: Date, options: Intl.DateTimeFormatOptions) => {
    return date.toLocaleDateString('es-ES', options);
  }, []);

  return {
    navigate,
    getDaysInMonth,
    getCalendarDays,
    getMonthNames,
    getYearRange,
    isToday,
    formatDate,
  };
};
